import Vue from "vue";

/**
 * Upload queue of the media service (HCIT-1105). It is global (not part of a component), so uploads go on
 * when the user switches folders or pages; the panel in the app shell shows them (ImgUploadPanel).
 *
 * An item goes WAITING -> UPLOADING (bytes are sent, `progress`) -> IN_PROGRESS (the media service
 * processes it) -> DONE or FAILED (`reason`: TOO_LARGE, UNSUPPORTED, TIMEOUT or the service's text).
 */
export const WAITING = "WAITING";
export const UPLOADING = "UPLOADING";
export const IN_PROGRESS = "IN_PROGRESS";
export const DONE = "DONE";
export const FAILED = "FAILED";

export const MAX_SIZE = 10 * 1024 * 1024;

// images and PDF, the media service checks the content
export const ACCEPT = "image/jpeg,image/png,image/gif,image/tiff,image/webp,image/heic,image/heif,application/pdf";
const TYPES = ACCEPT.split(",");
// browsers often report no type for these
const EXTENSIONS = ["heic", "heif", "tif", "tiff"];

export function isSupported (file) {
    const extension = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    return TYPES.includes(file.type) || (!file.type && EXTENSIONS.includes(extension));
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const isActive = (item) => item.state === WAITING || item.state === UPLOADING || item.state === IN_PROGRESS;

export function createQueue ({ upload, uploadState, concurrency = 3, pollInterval = 1500, pollAttempts = 60 }) {
    const state = Vue.observable({ items: [] });
    const doneListeners = [];
    const finishedListeners = [];
    let uploading = 0;
    let polling = false;
    let key = 0;

    const reasonOf = (error) => error && error.status === 413 ? "TOO_LARGE"
        : error && error.status === 415 ? "UNSUPPORTED"
            : null;

    function settle (item, newState, reason) {
        item.state = newState;
        item.reason = reason || null;
        if (newState === DONE) {
            doneListeners.forEach(listener => listener({ siteId: item.siteId, folderId: item.folderId }));
        }
        if (!state.items.some(isActive)) {
            const items = state.items;
            const result = { done: items.filter(i => i.state === DONE).length, failed: items.filter(i => i.state === FAILED).length };
            finishedListeners.forEach(listener => listener(result));
        }
    }

    function pump () {
        while (uploading < concurrency) {
            const next = state.items.find(item => item.state === WAITING);
            if (!next) {
                return;
            }
            send(next);
        }
    }

    async function send (item) {
        uploading++;
        item.state = UPLOADING;
        item.progress = 0;
        try {
            item.uploadId = await upload(item.siteId, item.folderId, item.file, fraction => item.progress = fraction);
            item.progress = 1;
            item.polls = 0;
            item.state = IN_PROGRESS;
            poll();
        } catch (error) {
            settle(item, FAILED, reasonOf(error));
        } finally {
            uploading--;
            pump();
        }
    }

    // One loop for all items the media service is processing
    async function poll () {
        if (polling) {
            return;
        }
        polling = true;
        try {
            while (state.items.some(item => item.state === IN_PROGRESS)) {
                await sleep(pollInterval);
                await Promise.all(state.items.filter(item => item.state === IN_PROGRESS).map(async item => {
                    try {
                        const result = await uploadState(item.uploadId);
                        if (result.status === "DONE") {
                            return settle(item, DONE);
                        }
                        if (result.status === "FAILED") {
                            return settle(item, FAILED, result.reason);
                        }
                    } catch (e) {
                        // try again
                    }
                    if (++item.polls >= pollAttempts) {
                        settle(item, FAILED, "TIMEOUT");
                    }
                }));
            }
        } finally {
            polling = false;
        }
    }

    return {
        state,

        /** Queues [files] for a folder: {siteId, folderId, siteLabel, folderLabel}. */
        add (target, files) {
            // earlier successes are gone from the list, failures stay until the user closes them
            if (!state.items.some(isActive)) {
                state.items = state.items.filter(item => item.state === FAILED);
            }

            for (const file of files) {
                const reason = file.size > MAX_SIZE ? "TOO_LARGE" : !isSupported(file) ? "UNSUPPORTED" : null;
                state.items.push({
                    key: ++key, ...target, file,
                    state: reason ? FAILED : WAITING, reason, progress: 0, uploadId: null, polls: 0,
                });
            }
            pump();
        },

        retry (item) {
            if (item.state === FAILED && item.reason !== "TOO_LARGE" && item.reason !== "UNSUPPORTED") {
                item.state = WAITING;
                item.reason = null;
                pump();
            }
        },

        /** Removes finished items (the panel's close). Items still running stay. */
        clear () {
            state.items = state.items.filter(isActive);
        },

        counts () {
            const items = state.items;
            return {
                total     : items.length,
                done      : items.filter(item => item.state === DONE).length,
                failed    : items.filter(item => item.state === FAILED).length,
                active    : items.filter(isActive).length,
            };
        },

        /** [listener]({siteId, folderId}) for every file the media service has processed. */
        onFileDone (listener) {
            doneListeners.push(listener);
            return () => doneListeners.splice(doneListeners.indexOf(listener), 1);
        },

        /** [listener]({done, failed}) when nothing is waiting or running any more. */
        onFinished (listener) {
            finishedListeners.push(listener);
            return () => finishedListeners.splice(finishedListeners.indexOf(listener), 1);
        },
    };
}
